import { useCallback, useRef, useState } from 'react';
import {
  Bold,
  Italic,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Link,
  Image,
  Minus,
  Table,
  Upload,
  Loader2,
  ChevronDown,
  Hash,
} from 'lucide-react';
import { Button } from './Button';
import { useUploadAttachment } from '@/hooks';
import toast from 'react-hot-toast';

interface MarkdownToolbarProps {
  editorRef: React.RefObject<any>;
  onInsert: (before: string, after: string, placeholder?: string) => void;
}

interface ToolbarButtonProps {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}

function ToolbarButton({ icon, title, onClick, disabled }: ToolbarButtonProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className="h-8 w-8 p-0"
    >
      {icon}
    </Button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-6 bg-border mx-1" />;
}

const CODE_LANGUAGES = [
  { id: 'javascript', label: 'JavaScript', ext: 'js' },
  { id: 'typescript', label: 'TypeScript', ext: 'ts' },
  { id: 'python', label: 'Python', ext: 'py' },
  { id: 'bash', label: 'Bash/Shell', ext: 'sh' },
  { id: 'json', label: 'JSON', ext: 'json' },
  { id: 'html', label: 'HTML', ext: 'html' },
  { id: 'css', label: 'CSS', ext: 'css' },
  { id: 'sql', label: 'SQL', ext: 'sql' },
  { id: 'go', label: 'Go', ext: 'go' },
  { id: 'rust', label: 'Rust', ext: 'rs' },
  { id: 'java', label: 'Java', ext: 'java' },
  { id: 'markdown', label: 'Markdown', ext: 'md' },
  { id: 'yaml', label: 'YAML', ext: 'yml' },
  { id: 'docker', label: 'Dockerfile', ext: 'dockerfile' },
];

interface CodeLanguageDropdownProps {
  onSelect: (lang: string, options?: { showLineNumbers?: boolean }) => void;
}

function CodeLanguageDropdown({ onSelect }: CodeLanguageDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelect = (lang: string) => {
    onSelect(lang);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  const handleBlur = (e: React.FocusEvent) => {
    if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
      setIsOpen(false);
    }
  };

  return (
    <div ref={dropdownRef} className="relative" onBlur={handleBlur}>
      <ToolbarButton
        icon={
          <div className="flex items-center gap-0.5">
            <Code className="h-4 w-4" />
            <ChevronDown className="h-3 w-3" />
          </div>
        }
        title="代码块（选择语言）"
        onClick={() => setIsOpen(!isOpen)}
      />
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-48 bg-popover border border-border rounded-lg shadow-lg z-50 py-1 max-h-64 overflow-y-auto">
          <div className="px-2 py-1.5 text-xs text-muted-foreground font-medium">选择语言</div>
          {CODE_LANGUAGES.map((lang) => (
            <button
              key={lang.id}
              className="w-full px-3 py-1.5 text-left text-sm hover:bg-muted transition-colors"
              onClick={() => handleSelect(lang.id)}
            >
              <span className="font-mono text-xs text-muted-foreground mr-2">.{lang.ext}</span>
              {lang.label}
            </button>
          ))}
          <div className="border-t mt-1 pt-1">
            <button
              className="w-full px-3 py-1.5 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2"
              onClick={() => onSelect('javascript', { showLineNumbers: true })}
            >
              <Hash className="h-3 w-3" />
              JavaScript + 行号
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

type ToolbarAction = {
  icon: React.ReactNode;
  title: string;
  action: () => void;
  disabled?: boolean;
} | {
  type: 'divider';
} | {
  type: 'dropdown';
  component: React.ReactNode;
};

export function MarkdownToolbar({ onInsert }: MarkdownToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAttachment = useUploadAttachment();
  const [isUploading, setIsUploading] = useState(false);

  const handleInsert = useCallback(
    (before: string, after: string = '', placeholder?: string) => {
      onInsert(before, after, placeholder);
    },
    [onInsert]
  );

  const handleCodeLanguageSelect = useCallback(
    (lang: string, options?: { showLineNumbers?: boolean }) => {
      const meta = options?.showLineNumbers ? ' showLineNumbers' : '';
      handleInsert(`\`\`\`${lang}${meta}\n`, '\n```\n', '代码块');
    },
    [handleInsert]
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('文件大小不能超过 10MB');
        return;
      }

      setIsUploading(true);

      try {
        const result = await uploadAttachment.mutateAsync(file);
        // Insert the markdown at cursor position
        onInsert(result.markdown, '');
        toast.success(`已上传: ${result.attachment.filename}`);
      } catch (error: any) {
        toast.error(error.message || '上传失败');
      } finally {
        setIsUploading(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [uploadAttachment, onInsert]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (!file) return;

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('文件大小不能超过 10MB');
        return;
      }

      setIsUploading(true);

      try {
        const result = await uploadAttachment.mutateAsync(file);
        onInsert(result.markdown, '');
        toast.success(`已上传: ${result.attachment.filename}`);
      } catch (error: any) {
        toast.error(error.message || '上传失败');
      } finally {
        setIsUploading(false);
      }
    },
    [uploadAttachment, onInsert]
  );

  const actions: ToolbarAction[] = [
    {
      icon: <Heading1 className="h-4 w-4" />,
      title: '标题 1',
      action: () => handleInsert('# ', ''),
    },
    {
      icon: <Heading2 className="h-4 w-4" />,
      title: '标题 2',
      action: () => handleInsert('## ', ''),
    },
    {
      icon: <Heading3 className="h-4 w-4" />,
      title: '标题 3',
      action: () => handleInsert('### ', ''),
    },
    { type: 'divider' },
    {
      icon: <Bold className="h-4 w-4" />,
      title: '加粗 (Ctrl+B)',
      action: () => handleInsert('**', '**', '粗体文本'),
    },
    {
      icon: <Italic className="h-4 w-4" />,
      title: '斜体 (Ctrl+I)',
      action: () => handleInsert('*', '*', '斜体文本'),
    },
    {
      icon: <Strikethrough className="h-4 w-4" />,
      title: '删除线',
      action: () => handleInsert('~~', '~~', '删除文本'),
    },
    { type: 'divider' },
    {
      icon: <Link className="h-4 w-4" />,
      title: '链接',
      action: () => handleInsert('[', '](url)', '链接文本'),
    },
    {
      icon: <Image className="h-4 w-4" />,
      title: '图片',
      action: () => handleInsert('![', '](url)', '图片描述'),
    },
    { type: 'divider' },
    {
      icon: <Code className="h-4 w-4" />,
      title: '行内代码',
      action: () => handleInsert('`', '`', '代码'),
    },
    {
      type: 'dropdown',
      component: <CodeLanguageDropdown key="code-lang-dropdown" onSelect={handleCodeLanguageSelect} />,
    },
    {
      icon: <Quote className="h-4 w-4" />,
      title: '引用',
      action: () => handleInsert('> ', ''),
    },
    { type: 'divider' },
    {
      icon: <List className="h-4 w-4" />,
      title: '无序列表',
      action: () => handleInsert('- ', ''),
    },
    {
      icon: <ListOrdered className="h-4 w-4" />,
      title: '有序列表',
      action: () => handleInsert('1. ', ''),
    },
    {
      icon: <Minus className="h-4 w-4" />,
      title: '分割线',
      action: () => handleInsert('\n---\n', ''),
    },
    { type: 'divider' },
    {
      icon: <Table className="h-4 w-4" />,
      title: '表格',
      action: () =>
        handleInsert(
          '\n| 标题1 | 标题2 | 标题3 |\n|-------|-------|-------|\n| 内容1 | 内容2 | 内容3 |\n',
          ''
        ),
    },
    {
      icon: isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />,
      title: isUploading ? '上传中...' : '上传附件 (支持拖拽)',
      action: handleUploadClick,
      disabled: isUploading,
    },
  ];

  return (
    <div
      className="flex items-center flex-wrap gap-0.5 p-2 border-b bg-muted/30"
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      onDragEnter={(e) => e.preventDefault()}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
        accept="image/*,.pdf,.doc,.docx,.txt,.md,.zip"
      />
      {actions.map((action, index) => {
        if ('type' in action && action.type === 'divider') {
          return <ToolbarDivider key={`divider-${index}`} />;
        }
        if ('type' in action && action.type === 'dropdown') {
          return action.component;
        }
        const btnAction = action as { icon: React.ReactNode; title: string; action: () => void; disabled?: boolean };
        return (
          <ToolbarButton
            key={btnAction.title}
            icon={btnAction.icon}
            title={btnAction.title}
            onClick={btnAction.action}
            disabled={btnAction.disabled}
          />
        );
      })}
    </div>
  );
}
